import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';

interface MultiselectAutoCompleteListProps<T> {
  options: T[];
  isLoading?: boolean;
  isSelected: (option: T) => boolean;
  getLabel: (option: T) => string;
  onToggle: (option: T, checked: boolean) => void;
  renderOption?: (option: T) => React.ReactNode;
}

export default function MultiselectAutoCompleteList<T>({
  options,
  isLoading,
  isSelected,
  onToggle,
  getLabel,
  renderOption,
}: MultiselectAutoCompleteListProps<T>) {
  // HOOKS
  const parentRef = useRef<HTMLDivElement | null>(null);

  // VIRTUALIZER
  const rowVirtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 8,
  });

  // RENDER
  return (
    <div ref={parentRef} className="max-h-[200px] overflow-y-auto" style={{ position: 'relative' }}>
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-[#0C8DBF]" />
        </div>
      )}

      {!isLoading && options.length === 0 && (
        <div className="flex justify-center py-4 text-[14px] text-[#9D9D9C]">No options found</div>
      )}

      {!isLoading && options.length > 0 && (
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
            width: '100%',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const option = options[virtualRow.index];
            const checked = isSelected(option);

            return (
              <div
                key={String(virtualRow.index + 1)}
                data-index={String(virtualRow.index)}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className={cn(
                  'flex items-center gap-2 p-1.5 cursor-pointer hover:bg-[#E4F8FF]',
                  checked && 'bg-[#E4F8FF]'
                )}
                onClick={() => onToggle(option, !checked)}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => onToggle(option, c as boolean)}
                  className="border-[#0C8DBF] data-[state=checked]:bg-[#0C8DBF] data-[state=checked]:border-white"
                />
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <span className="text-[12px] font-[400] text-[#4B4B4B]">{getLabel(option)}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
